import 'bootstrap/dist/css/bootstrap.css';
import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilePdf, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import Spinner from '../../../shared/spinner';
import ReactInputMask from 'react-input-mask';
import { format } from 'date-fns';
import BigDataService from '../../../services/bigDataService';
import CustomAlert from '../../../shared/customAlert';

interface DossieData {
  ElapsedMilliseconds: number;
  Evidences: any;
  QueryDate: string;
  QueryId: string;
  Result: {
    MatchKeys: string;
    RegistrationData: {
      Addresses: {
        Primary: {
          AddressMain: string;
          City: string;
          Complement: string;
          ComplementType: string;
          Country: string;
          LastUpdateDate: string;
          Neighborhood: string;
          Number: string;
          State: string;
          Title: string;
          Type: string;
          Typology: string;
          ZipCode: string;
        };
        Secondary: {
          AddressMain: string;
          City: string;
          Complement: string;
          ComplementType: string;
          Country: string;
          LastUpdateDate: string;
          Neighborhood: string;
          Number: string;
          State: string;
          Title: string;
          Type: string;
          Typology: string;
          ZipCode: string;
        };
      };
      BasicData: {
        validBirthDateInRFSource: boolean;
        BirthDate: string;
        CapturedBirthDateFromRFSource: string;
        Gender: string;
        IsValidBirthDateInRFSource: boolean;
        MotherName: string;
        Name: string;
        NameUniquenessScore: number;
        TaxIdNumber: string;
        TaxIdStatus: string;
        TaxIdStatusDate: string;
      };
      Emails: {
        Primary: {
          Domain: string;
          EmailAddress: string;
          LastUpdateDate: string;
          Type: string;
          UserName: string;
        };
        Secondary: any;
      };
      Phones: {
        Primary: {
          AreaCode: string;
          Complement: string;
          CountryCode: string;
          LastUpdateDate: string;
          Number: string;
          PhoneNumberOfEntities: number;
          Type: string;
        };
        Secondary: {
          AreaCode: string;
          Complement: string;
          CountryCode: string;
          LastUpdateDate: string;
          Number: string;
          PhoneNumberOfEntities: number;
          Type: string;
        };
      };
    };
  }[];
  Status: {
    registration_data: {
      Code: number;
      Message: string;
    }[];
  };
}

interface BasicDataLabels {
  validBirthDateInRFSource: string;
  BirthDate: string;
  CapturedBirthDateFromRFSource: string;
  Gender: string;
  IsValidBirthDateInRFSource: string;
  MotherName: string;
  Name: string;
  NameUniquenessScore: string;
  TaxIdNumber: string;
  TaxIdStatus: string;
  TaxIdStatusDate: string;
}

interface AddressLabels {
  AddressMain: string;
  City: string;
  Complement: string;
  ComplementType: string;
  Country: string;
  LastUpdateDate: string;
  Neighborhood: string;
  Number: string;
  State: string;
  Title: string;
  Type: string;
  Typology: string;
  ZipCode: string;
}

interface PhoneLabels {
  AreaCode: string;
  Complement: string;
  CountryCode: string;
  LastUpdateDate: string;
  Number: string;
  PhoneNumberOfEntities: string;
  Type: string;
}

interface EmailLabels {
  Domain: string;
  EmailAddress: string;
  LastUpdateDate: string;
  Type: string;
  UserName: string;
}

interface MetadataLabels {
  QueryId: string;
  QueryDate: string;
  ElapsedMilliseconds: string;
}

interface FieldLabels {
  BasicData: BasicDataLabels;
  Address: AddressLabels;
  Phone: PhoneLabels;
  Email: EmailLabels;
  Metadata: MetadataLabels;
}

const fieldLabels: FieldLabels = {
  BasicData: {
    validBirthDateInRFSource: 'Data de nascimento válida na fonte RF',
    BirthDate: 'Data de nascimento',
    CapturedBirthDateFromRFSource: 'Data capturada na fonte RF',
    Gender: 'Gênero',
    IsValidBirthDateInRFSource: 'Data válida na fonte RF',
    MotherName: 'Nome da mãe',
    Name: 'Nome',
    NameUniquenessScore: 'Índice de unicidade do nome',
    TaxIdNumber: 'CPF',
    TaxIdStatus: 'Status do CPF',
    TaxIdStatusDate: 'Data do status do CPF',
  },
  Address: {
    AddressMain: 'Logradouro',
    City: 'Cidade',
    Complement: 'Complemento',
    ComplementType: 'Tipo de complemento',
    Country: 'País',
    LastUpdateDate: 'Última atualização',
    Neighborhood: 'Bairro',
    Number: 'Número',
    State: 'Estado',
    Title: 'Título',
    Type: 'Tipo',
    Typology: 'Tipologia',
    ZipCode: 'CEP',
  },
  Phone: {
    AreaCode: 'DDD',
    Complement: 'Complemento',
    CountryCode: 'Código do país',
    LastUpdateDate: 'Última atualização',
    Number: 'Número',
    PhoneNumberOfEntities: 'Número de entidades',
    Type: 'Tipo',
  },
  Email: {
    Domain: 'Domínio',
    EmailAddress: 'E-mail',
    LastUpdateDate: 'Última atualização',
    Type: 'Tipo',
    UserName: 'Nome de usuário',
  },
  Metadata: {
    QueryId: 'ID da consulta',
    QueryDate: 'Data/hora da consulta',
    ElapsedMilliseconds: 'Tempo de processamento',
  }
};

function ConsultaDossieComponent() {
  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [dossieData, setDossieData] = useState<DossieData | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [exportLoading, setExportLoading] = useState({ pdf: false, csv: false });

  const bigDataService = new BigDataService();

  const handleSearch = async () => {
    if (!inputValue) {
      setAlert({ message: 'Preencha o campo de busca', type: 'warning' });
      return;
    }

    try {
      setDossieData(null);
      setHasSearched(true);
      setLoading(true);
      
      const cleanedCpf = inputValue.replace(/\D/g, '');
      const response = await bigDataService.consultarPorCpf(cleanedCpf);
      setDossieData(response);
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAlert({ message: 'Erro ao consultar dossiê', type: 'error' });
      console.error('Erro na consulta:', error);
    }
  };

  const handleGerarPDF = async () => {
    if (!inputValue) {
      setAlert({ message: 'Preencha o campo de CPF primeiro', type: 'warning' });
      return;
    }

    try {
      setExportLoading(prev => ({ ...prev, pdf: true }));
      const cleanedCpf = inputValue.replace(/\D/g, '');
      const pdfBlob = await bigDataService.gerarPdfPorCpf(cleanedCpf);
      
      // Criar link para download
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dossie-${cleanedCpf}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportLoading(prev => ({ ...prev, pdf: false }));
      setAlert({ message: 'PDF gerado com sucesso!', type: 'success' });
    } catch (error) {
      setExportLoading(prev => ({ ...prev, pdf: false }));
      setAlert({ message: 'Erro ao gerar PDF', type: 'error' });
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const handleGerarCSV = async () => {
    if (!inputValue) {
      setAlert({ message: 'Preencha o campo de CPF primeiro', type: 'warning' });
      return;
    }

    try {
      setExportLoading(prev => ({ ...prev, csv: true }));
      const cleanedCpf = inputValue.replace(/\D/g, '');
      const csvBlob = await bigDataService.gerarCsvPorCpf(cleanedCpf);
      
      // Criar link para download
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dossie-${cleanedCpf}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setExportLoading(prev => ({ ...prev, csv: false }));
      setAlert({ message: 'CSV gerado com sucesso!', type: 'success' });
    } catch (error) {
      setExportLoading(prev => ({ ...prev, csv: false }));
      setAlert({ message: 'Erro ao gerar CSV', type: 'error' });
      console.error('Erro ao gerar CSV:', error);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Não informado';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const renderField = (value: any, key: string) => {
    if (value === null || value === undefined) return 'Não informado';
    
    if (key.includes('Date')) {
      return formatDate(value);
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    
    if (value === '') {
      return 'Não informado';
    }
    
    return String(value);
  };

  const renderSection = (
    data: any,
    section: keyof FieldLabels,
    title: string
  ) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <div className="mb-4">
          <h6>{title}</h6>
          <p className="text-muted">Nenhum dado disponível</p>
        </div>
      );
    }

    const sectionLabels = fieldLabels[section];

    return (
      <div className="mb-4">
        <h6>{title}</h6>
        <table className="table table-bordered table-sm">
          <tbody>
            {Object.entries(data).map(([key, value]) => (
              <tr key={key}>
                <th style={{ width: '30%' }}>
                  {(sectionLabels as any)[key] || key}
                </th>
                <td>{renderField(value, key)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {alert && (
        <CustomAlert 
          message={alert.message} 
          type={alert.type} 
          onClose={() => setAlert(null)} 
        />
      )}
      
      <div className="container-fluid mt-3">
        <div className="card">
          <div className="card-header bg-light">
            <h5 className="mb-0">Consulta de Dossiê Completo</h5>
          </div>
          
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-8">
                <div className="input-group">
                  <ReactInputMask
                    className="form-control"
                    mask="999.999.999-99"
                    placeholder="Digite o CPF"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    {loading ? 'Buscando...' : 'Buscar'}
                  </button>
                </div>
              </div>
              
              <div className="col-md-4 d-flex gap-2">
                <button
                  className="btn btn-danger"
                  onClick={handleGerarPDF}
                  disabled={exportLoading.pdf || !inputValue}
                >
                  {exportLoading.pdf ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Gerando PDF...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFilePdf} className="me-2" />
                      Gerar PDF
                    </>
                  )}
                </button>
                
                <button
                  className="btn btn-success"
                  onClick={handleGerarCSV}
                  disabled={exportLoading.csv || !inputValue}
                >
                  {exportLoading.csv ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Gerando CSV...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFileCsv} className="me-2" />
                      Gerar CSV
                    </>
                  )}
                </button>
              </div>
            </div>

            {loading && <Spinner loading={loading} />}

            {hasSearched && !loading && (
              <div className="mt-4">
                {!dossieData ? (
                  <div className="alert alert-info">
                    Nenhum dado encontrado para este CPF
                  </div>
                ) : (
                  <>
                    {renderSection(
                      {
                        QueryId: dossieData.QueryId,
                        QueryDate: dossieData.QueryDate,
                        ElapsedMilliseconds: dossieData.ElapsedMilliseconds,
                      },
                      'Metadata',
                      'Metadados da Consulta'
                    )}

                    {dossieData.Status?.registration_data?.length > 0 && (
                      <div className="mb-4">
                        <h6>Status</h6>
                        <ul className="list-group">
                          {dossieData.Status.registration_data.map((status, i) => (
                            <li key={i} className="list-group-item">
                              {status.Message} (Código: {status.Code})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {dossieData.Result.map((result, index) => (
                      <div key={index} className="card mb-4">
                        <div className="card-header bg-light">
                          <h6 className="mb-0">Registro {index + 1}</h6>
                        </div>
                        <div className="card-body">
                          {result.MatchKeys && (
                            <div className="mb-3">
                              <strong>Chaves de correspondência:</strong>{' '}
                              {result.MatchKeys}
                            </div>
                          )}

                          {renderSection(
                            result.RegistrationData.BasicData,
                            'BasicData',
                            'Dados Básicos'
                          )}

                          {renderSection(
                            result.RegistrationData.Addresses.Primary,
                            'Address',
                            'Endereço Principal'
                          )}

                          {result.RegistrationData.Addresses.Secondary &&
                            Object.keys(result.RegistrationData.Addresses.Secondary).length > 0 &&
                            renderSection(
                              result.RegistrationData.Addresses.Secondary,
                              'Address',
                              'Endereço Secundário'
                            )}

                          {renderSection(
                            result.RegistrationData.Phones.Primary,
                            'Phone',
                            'Telefone Principal'
                          )}

                          {result.RegistrationData.Phones.Secondary &&
                            Object.keys(result.RegistrationData.Phones.Secondary).length > 0 &&
                            renderSection(
                              result.RegistrationData.Phones.Secondary,
                              'Phone',
                              'Telefone Secundário'
                            )}

                          {renderSection(
                            result.RegistrationData.Emails.Primary,
                            'Email',
                            'E-mail Principal'
                          )}

                          {result.RegistrationData.Emails.Secondary &&
                            Object.keys(result.RegistrationData.Emails.Secondary).length > 0 &&
                            renderSection(
                              result.RegistrationData.Emails.Secondary,
                              'Email',
                              'E-mail Secundário'
                            )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ConsultaDossieComponent;